import { useEffect, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SLUG_MAX_LENGTH, SLUG_PATTERN, normSlug, slugBlur, toSlug } from '@/lib/slug';
import {
  api,
  ENTITY_STATUSES,
  ENTITY_STATUS_LABELS,
  type AdminBusiness,
  type EntityStatus,
} from '@/lib/api';
import { ImageUploadField } from '@/components/ImageUploadField';
import { OpeningHoursField } from '@/components/OpeningHoursField';
import { LocationPickerMap } from '@/components/LocationPickerMap';
import { BusinessImagesSection } from '@/components/ImagesSection';
import { VideoUrlsField, isVideoLink } from '@/components/VideoUrlsField';

export const Route = createFileRoute('/_authed/businesses/$businessId')({
  component: BusinessDetailPage,
});

const schema = z.object({
  name: z.string().min(1).max(200),
  // A business without a village cannot appear on any public page: the site
  // groups businesses under the community they belong to. Empty string is the
  // "none" option and becomes null on save.
  villageId: z.string(),
  // Public profile fields. The business exists in the CMS from the moment it
  // honours a perk, so all of these are optional until someone publishes it.
  slug: z.preprocess(
    normSlug(),
    z
      .string()
      .max(SLUG_MAX_LENGTH, `At most ${SLUG_MAX_LENGTH} characters`)
      .regex(SLUG_PATTERN, 'Lowercase letters, digits and single hyphens')
      .or(z.literal('')),
  ),
  descriptionEn: z.string(),
  descriptionEl: z.string(),
  phone: z.string().max(40).optional(),
  websiteUrl: z.string().url().optional().or(z.literal('')),
  bookingUrl: z.string().url().optional().or(z.literal('')),
  lat: z.string().optional(),
  lng: z.string().optional(),
  isPublic: z.boolean(),
  code: z.preprocess(
    (v) => (typeof v === 'string' ? v.trim().toUpperCase() : v),
    z.string().regex(/^[A-Z0-9-]{2,32}$/u, '2–32 uppercase letters, digits, hyphens'),
  ),
  contactEmail: z.string().email().optional().or(z.literal('')),
  address: z.string().optional(),
  mapUrl: z.string().url().optional().or(z.literal('')),
  logoUrl: z.string().url().optional().or(z.literal('')),
  notes: z.string().optional(),
  status: z.enum(ENTITY_STATUSES as [EntityStatus, ...EntityStatus[]]),
});

type FormValues = z.infer<typeof schema>;

function BusinessDetailPage() {
  const { businessId } = Route.useParams();
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => api.getBusiness(businessId),
  });

  const save = useMutation({
    mutationFn: (body: Partial<AdminBusiness>) => api.updateBusiness(businessId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business', businessId] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });

  if (q.isLoading || !q.data) return <div className="card text-slate-gray">Loading…</div>;
  const b = q.data.business;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/businesses" className="btn-ghost -ml-3 mb-2">
          ← Back to businesses
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-h1">{b.name}</h1>
          <span className="rounded-full bg-light-gray px-2 py-0.5 font-mono text-caption text-slate-gray">
            {b.code}
          </span>
        </div>
      </div>

      <BusinessForm
        business={b}
        onSubmit={(v) => save.mutateAsync(v)}
        saving={save.isPending}
        saved={save.isSuccess}
        error={save.error ? String(save.error) : null}
      />

      <div className="card">
        <BusinessImagesSection businessId={businessId} />
      </div>
    </div>
  );
}

function BusinessForm({
  business,
  onSubmit,
  saving,
  saved,
  error,
}: {
  business: AdminBusiness;
  onSubmit: (v: Partial<AdminBusiness>) => Promise<unknown>;
  saving: boolean;
  saved: boolean;
  error: string | null;
}) {
  // The village list drives the selector below. Cached by react-query, so the
  // dozen businesses an editor opens in a row cost one request.
  const villagesQ = useQuery({ queryKey: ['villages'], queryFn: () => api.listVillages() });
  const villages = villagesQ.data?.villages ?? [];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty, errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: business.name,
      villageId: business.villageId ?? '',
      slug: business.slug ?? '',
      descriptionEn: business.description?.en ?? '',
      descriptionEl: business.description?.el ?? '',
      phone: business.phone ?? '',
      websiteUrl: business.websiteUrl ?? '',
      bookingUrl: business.bookingUrl ?? '',
      lat: business.lat != null ? String(business.lat) : '',
      lng: business.lng != null ? String(business.lng) : '',
      isPublic: business.isPublic,
      code: business.code,
      contactEmail: business.contactEmail ?? '',
      address: business.address ?? '',
      mapUrl: business.mapUrl ?? '',
      logoUrl: business.logoUrl ?? '',
      notes: business.notes ?? '',
      status: business.status,
    },
  });

  // The select is rendered before the village list arrives, holding only the
  // "not assigned" option, so the browser shows that — and does not change its
  // mind when the options appear. The form value itself was right all along,
  // but an editor looking at a published business saw it as unassigned. Put
  // the stored village back into the select once there is an option for it.
  const villagesLoaded = villagesQ.isSuccess;
  // Where the map opens while the business has no point of its own.
  const selectedVillage = villages.find((v) => v.id === watch('villageId'));
  const mapCentre = selectedVillage
    ? { lat: selectedVillage.lat, lng: selectedVillage.lng }
    : undefined;
  useEffect(() => {
    if (villagesLoaded) setValue('villageId', business.villageId ?? '', { shouldDirty: false });
  }, [villagesLoaded, business.villageId, setValue]);

  // Structured jsonb, so it sits beside the form rather than inside it.
  const [hours, setHours] = useState(business.openingHours ?? null);
  const [hoursDirty, setHoursDirty] = useState(false);
  // Same for the video list: an ordered array of links.
  const [videos, setVideos] = useState<string[]>(business.videoUrls ?? []);
  const [videosDirty, setVideosDirty] = useState(false);
  const [videosError, setVideosError] = useState<string | null>(null);

  const submit = async (v: FormValues) => {
    const videoUrls = videos.map((u) => u.trim()).filter(Boolean);
    if (videoUrls.some((u) => !isVideoLink(u))) {
      setVideosError('One of the videos is not a link — fix or remove it, then save.');
      return;
    }
    setVideosError(null);
    const { descriptionEn, descriptionEl, lat, lng, ...rest } = v;
    // A published business needs a slug to be addressable; deriving it from
    // the name beats blocking the save on a field most editors will not think
    // about. Same rule the migration used when it backfilled.
    const slug = v.slug || (v.isPublic ? toSlug(v.name) : '');
    await onSubmit({
      ...rest,
      villageId: v.villageId || null,
      slug: slug || null,
      // Keyed on either language, not on English alone: a description written
      // only in Greek used to be dropped on save without a word. `en` stays
      // (empty) because the schema requires it — same shape Experiences save.
      description:
        descriptionEn.trim() || descriptionEl.trim()
          ? {
              en: descriptionEn.trim(),
              ...(descriptionEl.trim() ? { el: descriptionEl.trim() } : {}),
            }
          : null,
      phone: v.phone || null,
      websiteUrl: v.websiteUrl || null,
      bookingUrl: v.bookingUrl || null,
      lat: lat ? Number(lat) : null,
      lng: lng ? Number(lng) : null,
      openingHours: hours,
      contactEmail: v.contactEmail || null,
      address: v.address || null,
      mapUrl: v.mapUrl || null,
      logoUrl: v.logoUrl || null,
      videoUrls,
      notes: v.notes || null,
    });
    setHoursDirty(false);
    setVideos(videoUrls);
    setVideosDirty(false);
    reset(v);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="card space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Name</label>
          <input className="input" {...register('name')} />
        </div>
        <div>
          <label className="label">Internal code (admin and redemption reports only)</label>
          <input className="input font-mono uppercase" {...register('code')} />
          {errors.code ? (
            <p className="mt-1 text-caption text-error">{errors.code.message}</p>
          ) : null}
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" {...register('status')}>
            {ENTITY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ENTITY_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Village</label>
          <select className="input" {...register('villageId')}>
            <option value="">— not assigned —</option>
            {villages.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name.en}
              </option>
            ))}
          </select>
          <p className="mt-1 text-caption text-slate-gray">
            Public pages group businesses by village; an unassigned business is invisible on the
            website.
          </p>
        </div>
        <div>
          <label className="label">Contact email</label>
          <input className="input" type="email" {...register('contactEmail')} />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Address</label>
          <input className="input" {...register('address')} />
        </div>
        <div>
          <label className="label">Google Maps pin URL</label>
          <input className="input" {...register('mapUrl')} />
        </div>
        <div className="sm:col-span-2">
          {/*
           * Watch + setValue glue: ImageUploadField is a controlled component
           * (it owns its own file-picker state), but react-hook-form holds
           * the form-level value. We bridge by reading the current `logoUrl`
           * via `watch` and writing back via `setValue` so the dirty / submit
           * tracking stays accurate.
           */}
          <ImageUploadField
            label="Logo"
            value={watch('logoUrl') ?? ''}
            onChange={(url) => setValue('logoUrl', url, { shouldDirty: true })}
            resourceType="businesses"
            helper="JPEG / PNG / WebP, used on the visitor reward screen and the admin business list."
          />
          {/* Hidden field so the form serialiser still sees `logoUrl` even
              though the visible input above is in the upload component. */}
          <input type="hidden" {...register('logoUrl')} />
        </div>
      </div>
      <fieldset className="space-y-4 rounded-card border border-neutral-gray p-4">
        <legend className="label px-1">Public website profile</legend>

        <label className="flex items-center gap-3">
          <input type="checkbox" className="h-4 w-4" {...register('isPublic')} />
          <span className="text-caption text-dark-navy">
            Show this business on the public website
          </span>
        </label>

        <p className="text-caption text-slate-gray">
          {!watch('isPublic')
            ? 'Not shown on the website.'
            : !watch('villageId')
              ? 'Pick a village above — the website has nowhere to put a business without one.'
              : 'Shown on the village page after the next publish.'}
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Slug (public URL)</label>
            <input className="input font-mono" placeholder="taverna-platanos" {...register('slug', slugBlur(setValue))} />
            {errors.slug ? (
              <p className="mt-1 text-caption text-error">{errors.slug.message}</p>
            ) : null}
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" placeholder="+357 99 123456" {...register('phone')} />
          </div>
          <div>
            <label className="label">Website</label>
            <input className="input" placeholder="https://…" {...register('websiteUrl')} />
          </div>
          <div>
            <label className="label">Booking link</label>
            <input className="input" placeholder="https://…" {...register('bookingUrl')} />
          </div>
        </div>

        {/* Same picker as villages and POIs: typing two decimals by hand was
            the one place an editor could put a taverna in the sea. Opens on
            the business's village until a point is set. Optional — a business
            with no point simply has no map on its page. */}
        <div className="space-y-2">
          <input type="hidden" {...register('lat')} />
          <input type="hidden" {...register('lng')} />
          {/* The picker reads its centre once, on mount: wait for the
              village list, and remount when the village changes. */}
          {!villagesLoaded ? (
            <p className="text-caption text-slate-gray">Loading map…</p>
          ) : (
          <LocationPickerMap
            key={watch('villageId') || 'none'}
            label="Location on the map"
            value={{ lat: Number(watch('lat')) || 0, lng: Number(watch('lng')) || 0 }}
            defaultCenter={mapCentre}
            helper={
              watch('lat') && watch('lng')
                ? 'Drag the marker or click the map to move it, or search by address.'
                : 'Not on the map yet — the marker shows the village centre. Click the map, drag the marker or search by address to place the business.'
            }
            onChange={(next) => {
              setValue('lat', next.lat.toFixed(6), { shouldDirty: true });
              setValue('lng', next.lng.toFixed(6), { shouldDirty: true });
            }}
          />
          )}
          {watch('lat') && watch('lng') ? (
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => {
                setValue('lat', '', { shouldDirty: true });
                setValue('lng', '', { shouldDirty: true });
              }}
            >
              Remove from the map
            </button>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Description — English</label>
            <textarea className="input min-h-[5rem]" rows={3} {...register('descriptionEn')} />
            {!watch('descriptionEn')?.trim() && watch('descriptionEl')?.trim() ? (
              <p className="mt-1 text-caption text-slate-gray">
                Empty English means the English page shows no description — only the Greek one
                does.
              </p>
            ) : null}
          </div>
          <div>
            <label className="label">Description — Ελληνικά</label>
            <textarea className="input min-h-[5rem]" rows={3} {...register('descriptionEl')} />
          </div>
        </div>

        <OpeningHoursField
          value={hours}
          onChange={(next) => {
            setHours(next);
            setHoursDirty(true);
          }}
        />

        <VideoUrlsField
          value={videos}
          onChange={(next) => {
            setVideos(next);
            setVideosDirty(true);
          }}
        />
      </fieldset>

      <div>
        <label className="label">Notes (internal — never shown publicly)</label>
        <textarea className="input min-h-[5rem]" rows={3} {...register('notes')} />
      </div>
      {videosError ?? error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {videosError ?? error}
        </div>
      ) : null}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="btn-primary"
          disabled={(!isDirty && !hoursDirty && !videosDirty) || saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && !isDirty && !hoursDirty && !videosDirty ? (
          <span className="text-caption text-success">Saved ✓</span>
        ) : null}
      </div>
    </form>
  );
}
